/**
 * Типы ответов бэкенда — зеркало DTO Nest.
 *
 * Живут в `shared`, а не в срезах сущностей, по одной причине: ответы
 * перестали совпадать со срезами. `MatchCardDto` содержит `myPredictions`,
 * то есть карточка матча несёт Прогнозы игрока. Положи этот тип
 * в `entities/match` — и он обязан импортировать `entities/prediction`,
 * а импорт между соседними срезами запрещён (скилл `fsd`).
 *
 * Здесь же окажется результат `pnpm api:types`, когда типы начнут
 * генерироваться из OpenAPI.
 *
 * Сверка с живой схемой — `pnpm api:contract`.
 */

/* ── enum'ы: зеркало prisma/enums бэкенда ────────────────────────────── */

export const MatchStatus = {
  SCHEDULED: "SCHEDULED",
  OPEN: "OPEN",
  CLOSED: "CLOSED",
  LIVE: "LIVE",
  FINISHED: "FINISHED",
  POSTPONED: "POSTPONED",
  CANCELLED: "CANCELLED",
  ABANDONED: "ABANDONED",
} as const;

export type MatchStatus = (typeof MatchStatus)[keyof typeof MatchStatus];

export const PredictionType = {
  MATCH_OUTCOME: "MATCH_OUTCOME",
  FIRST_GOAL: "FIRST_GOAL",
  EXACT_SCORE: "EXACT_SCORE",
  /** «Обе забьют»: бинарный выбор Да/Нет, второй Модуль. */
  BOTH_TEAMS_TO_SCORE: "BOTH_TEAMS_TO_SCORE",
  /**
   * «Тотал»: Да/Нет к вопросу «голов больше порога», второй Модуль.
   * Сам порог приходит с сервера в `AvailableType.totalLine` — в коде
   * фронта его быть не должно, это балансная настройка.
   */
  TOTAL_OVER_UNDER: "TOTAL_OVER_UNDER",
} as const;

export type PredictionType =
  (typeof PredictionType)[keyof typeof PredictionType];

export const MatchOutcome = {
  HOME: "HOME",
  DRAW: "DRAW",
  AWAY: "AWAY",
} as const;

export type MatchOutcome = (typeof MatchOutcome)[keyof typeof MatchOutcome];

export const FirstGoalOutcome = {
  HOME: "HOME",
  AWAY: "AWAY",
  NONE: "NONE",
} as const;

export type FirstGoalOutcome =
  (typeof FirstGoalOutcome)[keyof typeof FirstGoalOutcome];

export const PredictionStatus = {
  CONFIRMED: "CONFIRMED",
  WON: "WON",
  LOST: "LOST",
  VOID_WON: "VOID_WON",
} as const;

export type PredictionStatus =
  (typeof PredictionStatus)[keyof typeof PredictionStatus];

/* ── auth ────────────────────────────────────────────────────────────── */

/** `POST /auth/telegram` → AuthResponseDto. */
export interface AuthResponse {
  accessToken: string;
  /** Срок жизни токена строкой, как в JWT_TTL: `1h`. */
  expiresIn: string;
  isNewUser: boolean;
}

/* ── users ───────────────────────────────────────────────────────────── */

/** `GET /users/me` → UserProfileDto. BigInt приходят строками. */
export interface UserProfile {
  id: string;
  telegramId: string;
  displayName?: string;
  username?: string;
  firstName?: string;
  photoUrl?: string;
  isSubscribed: boolean;
  /** Номер телефона передан. Сам номер наружу не отдаётся никогда. */
  hasPhone: boolean;
  referralCode: string;
  referralStartParam: string;
  /**
   * Готовая ссылка-приглашение `https://t.me/<бот>/<приложение>?startapp=ref_КОД`.
   *
   * Собирает сервер: адрес мини-аппа живёт в его окружении и клиенту
   * не виден. `null` — адрес не настроен, звать друга пока некуда.
   */
  referralLink: string | null;
  /**
   * Ссылка на канал BetBoom для кнопок «Подпишись».
   *
   * Собирает сервер из `game_setting`: у публичного канала выводится
   * из `@имени`, у числового id нужна явная ссылка в парной настройке.
   * `null` — канал не настроен, и кнопку лучше не показывать вовсе,
   * чем вести в никуда.
   */
  channelUrl: string | null;
  /**
   * Метка кампании, с которой пришёл игрок. `null` — прямой заход.
   *
   * Ссылки источников по ТЗ ведут на БОТА (`t.me/бот?start=метка`),
   * а не на мини-апп: игрок может открыть приложение позже и другим
   * путём, и `start_param` там будет уже пустой. Метку помнит сервер.
   */
  acquisitionSource: string | null;
}

/**
 * `GET /users/me/summary` → UserSummaryDto.
 *
 * Один запрос вместо четырёх: показатели игрока размазаны по
 * `/predictions/funcoins`, `/leaderboard/weekly`, `/leaderboard/monthly`
 * и `/freebets`, а шапка видна на каждом экране.
 *
 * Места в рейтинге здесь нет намеренно: оно считается двумя COUNT(*)
 * по всей таблице состояний и слишком дорого для эндпоинта, который
 * зовётся на каждом старте. Место приходит в `/leaderboard/*`.
 */
export interface UserSummary {
  profile: UserProfile;
  /** Баланс Фанкоинов. Имя совпадает с `CreatePredictionResult`. */
  funcoinBalance: number;
  weeklyPoints: number;
  monthlyPoints: number;
  /** Начислено Фрибетов за текущий Месячный цикл. */
  freebetsEarned: number;
  /**
   * Идёт ли сезон и цикл прямо сейчас. `false` — счётчики нулевые не потому,
   * что игрок новый, а потому, что периода нет: вход работает до старта
   * кампании, и сводка обязана деградировать, а не падать.
   */
  hasActivePeriod: boolean;
}

/* ── predictions ─────────────────────────────────────────────────────── */

/** PredictionDto. */
export interface Prediction {
  id: string;
  matchId: string;
  type: PredictionType;
  status: PredictionStatus;
  outcome?: MatchOutcome;
  firstGoal?: FirstGoalOutcome;
  homeScoreGuess?: number;
  awayScoreGuess?: number;
  /** Для BOTH_TEAMS_TO_SCORE: true — «Да», false — «Нет». */
  bothTeamsScoreGuess?: boolean;
  /** Для TOTAL_OVER_UNDER: true — «больше», false — «меньше». */
  totalOverGuess?: boolean;
  /** Порог тотала, снятый при подтверждении: расчёт идёт по нему. */
  totalLine?: number;
  /** Начислено Очков: 0 до расчёта и при неуспехе. */
  pointsAwarded: number;
  createdAt: string;
}

/** Тело `POST /predictions` → CreatePredictionDto. */
export interface CreatePredictionPayload {
  matchId: string;
  type: PredictionType;
  outcome?: MatchOutcome;
  firstGoal?: FirstGoalOutcome;
  homeScoreGuess?: number;
  awayScoreGuess?: number;
  bothTeamsScoreGuess?: boolean;
  totalOverGuess?: boolean;
}

/** CreatePredictionResponseDto. */
export interface CreatePredictionResult {
  prediction: Prediction;
  /** Баланс Фанкоинов ПОСЛЕ списания. */
  funcoinBalance: number;
}

/**
 * `GET /predictions/funcoins` → FuncoinBalanceDto.
 *
 * Баланс сезонный и накопительный: хранится, а не вычисляется из лимита
 * и расхода, как было у Попыток (ГДД §4.3).
 */
export interface FuncoinBalance {
  balance: number;
}

export const PopupScenario = {
  SUCCESS: "SUCCESS",
  FAILURE: "FAILURE",
} as const;

export type PopupScenario = (typeof PopupScenario)[keyof typeof PopupScenario];

/** `GET /predictions/popup` → PredictionPopupDto. */
export interface PredictionPopup {
  hasResults: boolean;
  scenario?: PopupScenario;
  title?: string;
  body?: string;
  totalPoints?: number;
  predictions?: Prediction[];
}

/* ── derby ──────────────────────────────────────────────────────── */

export const DerbyOption = {
  A: "A",
  B: "B",
} as const;

export type DerbyOption = (typeof DerbyOption)[keyof typeof DerbyOption];

/**
 * Вопрос Дерби в том виде, в каком его отдаёт сервер.
 *
 * Правильного варианта здесь НЕТ намеренно: он приходит только в ответе
 * на `POST /derby/answer`, уже после выбора игрока (ГДД §6.2).
 */
export interface DerbyQuestion {
  id: number;
  /** Порядковый номер, 1..5. Отвечать можно только по порядку. */
  position: number;
  /**
   * Формулировка вопроса. Приходит только у ОТВЕЧЕННОГО и у ОТКРЫТОГО:
   * читать вперёд нельзя. Иначе таймер, стартующий по «Следующий»,
   * не ограничивал бы ничего — можно было бы прочитать все пять
   * вопросов, поискать ответы и только потом начать отвечать.
   *
   * Позиция приходит всегда: экрану нужно знать, сколько вопросов
   * и какой по счёту идёт.
   */
  text?: string;
  /** Вариант А. Виден вместе с текстом. */
  optionA?: string;
  /** Вариант Б. Виден вместе с текстом. */
  optionB?: string;
  /** Ответ игрока, если он уже дан. */
  chosenOption?: DerbyOption;
  /** Правильность — только для уже отвеченного вопроса. */
  isCorrect?: boolean;
  /**
   * Факт по теме вопроса — показывается вместе с правильностью, сразу
   * после ответа, и повторно в развёрнутой статистике на экране итога.
   * Как и `isCorrect`, приходит только для уже отвеченного вопроса —
   * до ответа текст читался бы как подсказка.
   */
  factText?: string;
}

/**
 * `GET /derby/current` → DerbyStateDto.
 *
 * `null` в теле ответа означает, что открытого Дерби сейчас нет:
 * механика идёт окнами между Сериями прогнозов (ГДД §6.4).
 */
export interface DerbyState {
  id: number;
  title?: string;
  opensAt: string;
  closesAt: string;
  questions: DerbyQuestion[];
  /** Вопрос, на который игрок отвечает сейчас. Пусто — Дерби пройден. */
  currentPosition?: number;
  correctCount: number;
  isFinished: boolean;
  /** Очки за прохождение. Приходят только у завершённого Дерби. */
  pointsAwarded?: number;
  /**
   * Награда за все правильные ответы. Лесенка своя у каждого Дерби,
   * поэтому число приходит с сервера, а не живёт в тексте на клиенте.
   * Пусто — лесенка не заведена, и о награде лучше промолчать.
   */
  maxPoints?: number;
  /**
   * Максимум Очков за ОДИН правильный ответ — наибольшая ступень лесенки.
   *
   * Отдельное поле, потому что это другое число: `maxPoints` — награда
   * за всё прохождение, и подставлять её в подпись «за каждый ответ»
   * значит обещать игроку кратно больше, чем он получит.
   *
   * Пусто — обещать за ответ нечего: лесенки нет либо награда одинакова
   * при любом числе угаданного.
   */
  maxPointsPerAnswer?: number;
  /**
   * Показать ли приглашение «Дерби началось».
   *
   * Решает сервер: Дерби открыто, игрок его ещё не прошёл и сегодня
   * приглашение не закрывал — не чаще раза в игровой день. Закрытие
   * отмечается `POST /derby/nudge/seen`, назавтра приглашение приходит снова.
   */
  shouldNudge: boolean;
  /**
   * Очки, которые игрок получил бы, закончись Дерби прямо сейчас —
   * ступень лесенки за текущее число правильных ответов. Показывается
   * рядом с «Правильно»/«Неправильно» на промежуточном экране.
   *
   * Считает сервер: лесенка своя у каждого Дерби, это балансная
   * константа, и на клиенте её нет. Пусто — лесенка не заведена.
   */
  pointsSoFar?: number;
  /**
   * Дедлайн ответа на ТЕКУЩИЙ вопрос — у каждого вопроса свой отсчёт,
   * а не общий до `closesAt` Дерби целиком. Не успел до дедлайна —
   * сервер защитывает вопрос как неверный: в `questions` появляется
   * запись с `isCorrect: false` и БЕЗ `chosenOption` (выбора не было),
   * и отсчёт следующего вопроса идёт от этого же дедлайна, а не от
   * возвращения игрока.
   *
   * Пусто в двух случаях: Дерби пройден или ещё не начат. Отсчёт
   * начинает `POST /derby/start`, а не это чтение: `GET /derby/current`
   * зовёт и экран матчей ради карточки Дерби.
   */
  currentDeadline?: string;
  /**
   * Сколько секунд дано на текущий вопрос целиком. Приходит вместе
   * с `currentDeadline` и пуст вместе с ним.
   *
   * Нужен кольцу-таймеру: по одному дедлайну доля оставшегося времени
   * не считается, а после перезагрузки кольцо начинало бы с полного.
   */
  currentTimeLimitSeconds?: number;
}

/**
 * `POST /derby/start` → DerbyStateDto.
 *
 * Тело запроса пустое, ответ — ровно то же, что у `GET /derby/current`,
 * включая `null` при отсутствии открытого Дерби. Вызывать при открытии
 * экрана Дерби ВМЕСТО чтения: с этого момента идёт таймер первого
 * вопроса, и в ответе приходит его `currentDeadline`. Идемпотентен —
 * перезагрузка экрана таймер не перезапускает.
 *
 * Читать состояние где угодно ещё (карточка Дерби в ленте матчей)
 * по-прежнему нужно через `GET /derby/current`: он таймер не запускает.
 */
export type StartDerbyResult = DerbyState | null;

/** Тело `POST /derby/answer`. */
export interface AnswerDerbyPayload {
  questionId: number;
  option: DerbyOption;
}

/** `POST /derby/answer` → AnswerResultDto. */
export interface AnswerDerbyResult {
  isCorrect: boolean;
  /** Правильный вариант — показывается сразу после ответа (ГДД §6.2). */
  correctOption: DerbyOption;
  /** Факт по теме вопроса — тот же текст, что осядет в `DerbyQuestion.factText`. */
  factText?: string;
  /** Следующий вопрос. Пусто — это был последний. */
  nextPosition?: number;
  /** Очки на этот момент — та же ступень лесенки, что в `DerbyState`. */
  pointsSoFar?: number;
  isFinished: boolean;
  correctCount: number;
  /** Итоговая награда одной суммой, если Дерби завершён. */
  pointsAwarded?: number;
}

/**
 * `POST /users/me/subscription/refresh` → SubscriptionStatusDto.
 *
 * Тело запроса пустое. Спрашивает Telegram заново и, если подписка
 * подтвердилась, засчитывает Ачивку за неё.
 *
 * Зачем отдельно от входа: статус проверяется при логине, а токен живёт
 * час. Игрок, подписавшийся после входа, до этого часами видел
 * «не подписан» — вместе с невыданной Ачивкой и её Очками. Звать при
 * возвращении в приложение и только когда `isSubscribed` сейчас `false`:
 * у подписанного проверка отрабатывает по кэшу и в сеть не ходит.
 */
export interface SubscriptionStatus {
  isSubscribed: boolean;
  /** Когда статус подтверждён, ISO. */
  checkedAt: string;
}

/* ── matches ─────────────────────────────────────────────────────────── */

export interface Team {
  id: number;
  name: string;
  shortName?: string;
  logoUrl?: string;
}

/**
 * Тип Прогноза, доступный на этом матче (AvailableTypeDto).
 *
 * Набор типов задаётся Модулем текущего Месячного цикла и меняется —
 * поэтому решает сервер, а не константа на клиенте.
 */
export interface AvailablePredictionType {
  type: PredictionType;
  /** Базовая награда до Множителя. */
  basePoints: number;
  /** Сколько Фанкоинов стоит Прогноз этого типа. Одинаково для всех типов. */
  funcoinCost: number;
  /** Для EXACT_SCORE: границы счёта. */
  minScoreValue?: number;
  maxScoreValue?: number;
  /**
   * Для TOTAL_OVER_UNDER: порог тотала, «больше» значит строго больше него.
   * Приходит с сервера, чтобы подпись вопроса не жила в коде фронта.
   */
  totalLine?: number;
  /** ГДД §5.3: не более одного Прогноза каждого типа на матч. */
  alreadyPredicted: boolean;
}

/** `GET /matches/open` и `GET /matches/finished` → MatchCardDto. */
/**
 * Сколько игроков выбрало каждый исход — только для `MATCH_OUTCOME`.
 *
 * Счётчики, а не проценты: процент — это оформление, и округлять его так,
 * чтобы три доли давали ровно 100, каждый потребитель волен по-своему.
 * Счётчик же одинаков для всех и проверяем.
 *
 * У остальных типов распределения нет: у «Точного счёта» вариантов сто на
 * сто, полоска из них не собирается, а «Первый гол» в ките такого блока
 * не имеет.
 */
export interface OutcomeDistribution {
  home: number;
  draw: number;
  away: number;
  /** Сумма трёх счётчиков. Ноль — распределение показывать нечего. */
  total: number;
}

/**
 * Распределение Прогнозов TOTAL_OVER_UNDER («Прогноз игроков») — тот же
 * принцип, что у OutcomeDistribution, но на два счётчика: выбор бинарный.
 */
export interface TotalDistribution {
  over: number;
  under: number;
  /** Сумма двух счётчиков. Ноль — распределение показывать нечего. */
  total: number;
}

export interface MatchCard {
  id: string;
  competition: Team;
  homeTeam: Team;
  awayTeam: Team;
  /** UTC ISO. */
  startsAt: string;
  /** UTC ISO. Отсечку определяет сервер, а не таймер на клиенте. */
  predictionsCloseAt: string;
  isOpenForPredictions: boolean;
  status: MatchStatus;
  homeScore?: number;
  awayScore?: number;
  availableTypes: AvailablePredictionType[];
  /** Собственные Прогнозы игрока на этот матч. */
  myPredictions: Prediction[];
  /**
   * Распределение Прогнозов на исход по всем игрокам. Приходит всегда,
   * даже нулевое: отсутствие поля и «никто ещё не выбрал» — разные вещи,
   * и клиенту не придётся их различать.
   */
  outcomeDistribution: OutcomeDistribution;
  /** Приходит всегда, как и outcomeDistribution: нулевое — тоже ответ. */
  totalDistribution: TotalDistribution;
}

/* ── розыгрыши ───────────────────────────────────────────────────────── */

export const DrawStatus = {
  PENDING: "PENDING",
  /** Пулы сформированы, подписки проверены. */
  POOLS_BUILT: "POOLS_BUILT",
  COMPLETED: "COMPLETED",
} as const;

export type DrawStatus = (typeof DrawStatus)[keyof typeof DrawStatus];

export const DrawPoolType = {
  TOP_10: "TOP_10",
  TOP_100: "TOP_100",
  TOP_1000: "TOP_1000",
} as const;

export type DrawPoolType = (typeof DrawPoolType)[keyof typeof DrawPoolType];

export const PrizeKind = {
  FREEBETS: "FREEBETS",
  PHYSICAL: "PHYSICAL",
  PHYSICAL_WITH_FREEBETS: "PHYSICAL_WITH_FREEBETS",
  /** Главный приз сезона — поездка. */
  SEASON_TRIP: "SEASON_TRIP",
} as const;

export type PrizeKind = (typeof PrizeKind)[keyof typeof PrizeKind];

export const DrawExclusionReason = {
  NOT_SUBSCRIBED: "NOT_SUBSCRIBED",
  WON_HIGHER_POOL: "WON_HIGHER_POOL",
  BLOCKED: "BLOCKED",
} as const;

export type DrawExclusionReason =
  (typeof DrawExclusionReason)[keyof typeof DrawExclusionReason];

/** PrizeDto. */
export interface Prize {
  code: string;
  title: string;
  description: string | null;
  kind: PrizeKind;
  /** Сколько Фрибетов входит в приз. */
  freebetAmount: number;
  imageUrl: string | null;
}

/** MonthlyDrawPoolDto. */
export interface MonthlyDrawPool {
  type: DrawPoolType;
  rankFrom: number;
  rankTo: number;
  winnersCount: number;
  /** Сколько получилось: участников могло не хватить (ГДД §11.2). */
  actualWinnersCount: number;
  prize: Prize | null;
  /** Игрок вошёл в пул по месту в рейтинге. */
  isParticipant: boolean;
  /**
   * Игрок попал в выборку. Без подписки место сохраняется, а в жеребьёвку
   * игрок не идёт — «в пуле» и «участвует» это разные вещи (ГДД §11.2).
   * До жеребьёвки всегда `false`: выборка ещё не сформирована.
   */
  isEligible: boolean;
  exclusionReason: DrawExclusionReason | null;
}

/** Пул в анонсе: границы мест и запланированный приз. */
export interface CurrentDrawPool {
  type: DrawPoolType;
  rankFrom: number;
  rankTo: number;
  winnersCount: number;
  /** `null` — приз на пул ещё не назначен в админке. */
  prize: Prize | null;
}

/**
 * `GET /draws/current` → CurrentDrawsDto. `null` — активного цикла нет.
 *
 * Что разыгрывается СЕЙЧАС. Не путать с `MonthlyDraw`: тот показывает
 * результат уже проведённого розыгрыша, то есть весь следующий месяц —
 * призы предыдущего.
 */
export interface CurrentDraws {
  cycleIndex: number;
  cycleTitle: string | null;
  cycleStartsAt: string;
  /** Исключающая граница. */
  cycleEndsAt: string;
  pools: CurrentDrawPool[];
  seasonPrize: Prize | null;
  seasonWinningClub: string | null;
  seasonTripCity: string | null;
}

/**
 * `GET /draws/monthly` → MonthlyDrawDto.
 *
 * `null` — ни один Месячный цикл ещё не завершился: розыгрыш
 * материализуется при подведении итогов, а не заранее.
 */
export interface MonthlyDraw {
  cycleIndex: number;
  status: DrawStatus;
  conductedAt: string | null;
  /** Место в Месячном рейтинге на момент фиксации. */
  myRank: number | null;
  pools: MonthlyDrawPool[];
  /** `null` — не выиграл или жеребьёвка ещё не проведена. */
  myPrize: {
    poolType: DrawPoolType;
    prize: Prize | null;
    freebetsAwarded: number;
  } | null;
}

/** `GET /draws/season-final` → SeasonFinalDto. `null` — не подготовлен. */
export interface SeasonFinalDraw {
  seasonCode: string;
  status: DrawStatus;
  conductedAt: string | null;
  isParticipant: boolean;
  isEligible: boolean;
  exclusionReason: DrawExclusionReason | null;
  isSubscribed: boolean | null;
  /** Снимок выполнения условий: `{ "caper": true, ... }`. */
  achievements: unknown;
  prize: Prize | null;
  winningClub: string | null;
  tripCity: string | null;
  isWinner: boolean;
}

/* ── рейтинги ────────────────────────────────────────────────────────── */

/** LeaderboardEntryDto — строка чужого места в таблице. */
export interface LeaderboardEntry {
  /** BigInt строкой: приведение к number округлит большие id. */
  userId: string;
  rank: number;
  points: number;
  displayName?: string;
  photoUrl?: string;
}

/** PrizeTierDto — диапазон мест Недельного турнира и его награда. */
export interface PrizeTier {
  rankFrom: number;
  rankTo: number;
  pointsReward: number;
  /** Номинал Фрибетов. Ноль — значение ещё не задано заказчиком. */
  freebetReward: number;
}

/**
 * DrawPoolDto лидерборда — не то же, что `MonthlyDrawPool`.
 *
 * У бэкенда два DTO про пулы Месячного розыгрыша: лидерборд отдаёт только
 * границы мест (`DrawPoolDto`, 4 поля), а `/draws/monthly` — полную карточку
 * с призом и участием игрока (`MonthlyDrawPoolDto`, 9 полей). Раньше здесь
 * стоял второй тип, и фронт считал, что у пулов лидерборда есть `prize`,
 * которого в ответе нет.
 */
export interface LeaderboardPool {
  type: DrawPoolType;
  rankFrom: number;
  rankTo: number;
  winnersCount: number;
}

/** MyPositionDto — место самого игрока. */
export interface MyPosition {
  /**
   * `null`, пока Очков нет. Именно `null`, а не отсутствующее поле:
   * `undefined` не сериализуется в JSON, и проверка `rank === null`
   * уходила бы в ветку «игрок в рейтинге».
   */
  rank: number | null;
  points: number;
  /** Всего участников с ненулевыми Очками. */
  totalPlayers: number;
  /** Игрок в диапазоне мест, за которые есть награда. */
  inPrizeZone: boolean;
  /** Самый ценный пул розыгрыша, в который игрок входит сейчас. */
  pool?: DrawPoolType;
}

/**
 * `GET /leaderboard/weekly` и `GET /leaderboard/monthly` → LeaderboardDto.
 *
 * Одна форма на оба рейтинга, но наполнение разное: у недельного заполнен
 * `prizeTiers` (сетка турнира) и пуст `pools`, у месячного — наоборот
 * (границы Топ-10/100/1000, ГДД §10.3).
 */
export interface Leaderboard {
  entries: LeaderboardEntry[];
  me: MyPosition;
  totalPlayers: number;
  prizeTiers: PrizeTier[];
  pools: LeaderboardPool[];
}

/** Пагинация обоих рейтингов. По умолчанию `limit: 100`, максимум 200. */
export interface LeaderboardQuery {
  limit?: number;
  offset?: number;
}

/* ── активность в сообществе ─────────────────────────────────────────── */

export const ActivityType = {
  CHANNEL_COMMENT: "CHANNEL_COMMENT",
  CHAT_MESSAGE: "CHAT_MESSAGE",
  REACTION_GIVEN: "REACTION_GIVEN",
  REACTION_RECEIVED: "REACTION_RECEIVED",
  REFERRAL: "REFERRAL",
} as const;

export type ActivityType = (typeof ActivityType)[keyof typeof ActivityType];

/** Одна строка «Сегодня доступно». */
export interface DailyActivityItem {
  type: ActivityType;
  awardedToday: number;
  /** У REFERRAL 0 означает отсутствие дневного ограничения. */
  dailyLimit: number;
  /** Сколько Очков даст следующее действие — уже с Множителем игрока. */
  pointsPerAction: number;
}

/**
 * Прогнозы за игровой день.
 *
 * Дневного лимита Прогнозов в игре нет: потолок задаёт пул дня Серии.
 * `available` считается по ВСЕМУ пулу, включая матчи с закрытым приёмом,
 * иначе знаменатель уменьшался бы в течение дня.
 */
export interface DailyPredictions {
  made: number;
  available: number;
}

/** `GET /community/daily-activity` → DailyActivityDto. */
export interface DailyActivity {
  /** `false` — нули оттого, что кампании нет, а не игрок бездействовал. */
  hasActivePeriod: boolean;
  gameDayLabel: string;
  predictions: DailyPredictions;
  items: DailyActivityItem[];
  /** `null` — ссылка на чат не задана в настройках. */
  chatUrl: string | null;
}

/* ── итоги периодов ──────────────────────────────────────────────────── */

export const PeriodResultKind = {
  WEEKLY_TOURNAMENT: "WEEKLY_TOURNAMENT",
  MONTHLY_DRAW: "MONTHLY_DRAW",
  SEASON_FINAL: "SEASON_FINAL",
} as const;

export type PeriodResultKind =
  (typeof PeriodResultKind)[keyof typeof PeriodResultKind];

/**
 * Итог периода: голые факты.
 *
 * Раскладку модалки выбирает клиент — вариантов вёрстки девять, а
 * доменных сценариев столько нет: «первое место» и «второе» отличаются
 * оформлением, а не правилом игры.
 */
export interface PeriodResult {
  kind: PeriodResultKind;
  /** Ключ для `POST /results/seen`. */
  refId: number;
  settledAt: string;
  periodStartsAt: string;
  /** `null` — периода больше нет. */
  nextPeriodStartsAt: string | null;
  /**
   * Место на момент фиксации. `null` у Финала сезона: это жеребьёвка
   * среди допущенных, и место в рейтингах на неё не влияет вовсе
   * (ГДД §11.3) — подставить сюда число значило бы его выдумать.
   */
  rank: number | null;
  points: number;
  pointsAwarded: number;
  freebetsAwarded: number;
  /** Фрибеты полагались, но сгорели без подписки. */
  freebetsForfeited: number;
  isWinner: boolean;
  prizeTitle: string | null;
  prizeKind: PrizeKind | null;
}

/**
 * `GET /results/pending` → PendingResultDto.
 *
 * Конверт, а не голый `null`: стор иначе не отличает «ещё не спрашивали»
 * от «показывать нечего».
 */
export interface PendingResult {
  hasResult: boolean;
  result: PeriodResult | null;
}

/** Тело `POST /results/seen`. */
export interface MarkResultSeenPayload {
  kind: PeriodResultKind;
  refId: number;
}

/** `POST /results/seen` → ResultSeenResponseDto. */
export interface ResultSeenResult {
  /** `false` — отметка уже стояла. */
  seen: boolean;
}

/* ── Карьера ────────────────────────────────────────────────────── */

export const RewardType = {
  FUNCOINS: "FUNCOINS",
  ACTIVITY_MULTIPLIER: "ACTIVITY_MULTIPLIER",
} as const;

export type RewardType = (typeof RewardType)[keyof typeof RewardType];

/** EncounterDto — точка в Карьере (ГДД §8.2). */
export interface Encounter {
  id: number;
  /** Порядок в Карьере. */
  position: number;
  /** Порог Очков месяца, на котором Энкаунтер считается пройденным. */
  pointsThreshold: number;
  rewardType: RewardType;
  /**
   * Значение награды строкой: для `FUNCOINS` это целое, для
   * `ACTIVITY_MULTIPLIER` — десятичная дробь вида `"1.50"`, и число
   * с плавающей точкой её бы исказило.
   */
  rewardValue: string;
  title?: string;
  description?: string;
  iconUrl?: string;
}

/**
 * `GET /reward-track` → RewardTrackDto.
 *
 * Глава соответствует Месячному циклу и начинается с нуля (ГДД §8.4):
 * `points` — это Очки ТЕКУЩЕГО месяца, а не накопленные за сезон.
 */
export interface RewardTrack {
  chapterIndex: number;
  chapterTitle: string;
  /** Очки месяца — они же прогресс Главы. */
  points: number;
  claimed: Encounter[];
  upcoming: Encounter[];
  /** Ближайший непройденный. Нет — Глава пройдена целиком. */
  next?: Encounter;
  pointsToNext?: number;
  /** Начало Главы — оно же начало Месячного цикла. */
  chapterStartsAt: string;
  /**
   * Конец Главы, ИСКЛЮЧАЮЩИЙ: это первый момент следующего цикла,
   * а не последняя его секунда.
   */
  chapterEndsAt: string;
  /** Когда начнётся следующая. `null` — Глава последняя в сезоне. */
  nextChapterStartsAt: string | null;
}

/* ── Ачивки ──────────────────────────────────────────────────────────── */

export const AchievementStatus = {
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
} as const;

export type AchievementStatus =
  (typeof AchievementStatus)[keyof typeof AchievementStatus];

export const AchievementConditionType = {
  PREDICTIONS_MADE: "PREDICTIONS_MADE",
  SUCCESSFUL_PREDICTIONS: "SUCCESSFUL_PREDICTIONS",
  CHANNEL_COMMENTS: "CHANNEL_COMMENTS",
  CHAT_MESSAGES: "CHAT_MESSAGES",
  REACTIONS_GIVEN: "REACTIONS_GIVEN",
  REACTIONS_RECEIVED: "REACTIONS_RECEIVED",
  REFERRALS: "REFERRALS",
  CHANNEL_SUBSCRIPTION: "CHANNEL_SUBSCRIPTION",
  DAILY_CHAT_MESSAGE_STREAK: "DAILY_CHAT_MESSAGE_STREAK",
  POINTS_TOTAL: "POINTS_TOTAL",
  /** Пройденные Дерби; `params.perfectOnly` — только без ошибок. */
  DERBY_COMPLETED: "DERBY_COMPLETED",
  /** Заходил в мини-апп N дней подряд. */
  DAILY_APP_ENTER_STREAK: "DAILY_APP_ENTER_STREAK",
  /** Приглашённые, набравшие порог Очков за текущий Месячный цикл. */
  QUALIFIED_REFERRALS: "QUALIFIED_REFERRALS",
  /** Полученные награды за Популярные посты канала. */
  POPULAR_POST_AWARDS: "POPULAR_POST_AWARDS",
  /** Сколько раз игрок забустил канал. */
  CHANNEL_BOOSTS: "CHANNEL_BOOSTS",
  /** Подписка на внешний канал партнёра. */
  EXTERNAL_CHANNEL_SUBSCRIPTION: "EXTERNAL_CHANNEL_SUBSCRIPTION",
} as const;

export type AchievementConditionType =
  (typeof AchievementConditionType)[keyof typeof AchievementConditionType];

/** AchievementConditionDto — одно условие с собственным прогрессом. */
export interface AchievementCondition {
  type: AchievementConditionType;
  /**
   * Подпись для игрока: «Комментарии», «Дерби без ошибок».
   * Считает сервер — сужения условия лежат в его `params`.
   */
  label: string;
  targetValue: number;
  currentValue: number;
  isCompleted: boolean;
  /**
   * Ссылка на канал для кнопки «Подписаться».
   *
   * Есть только у условия про ЧУЖОЙ канал (`EXTERNAL_CHANNEL_SUBSCRIPTION`):
   * свой канал открывается ссылкой из сводки игрока, а партнёрский знает
   * только сервер — подменять его «своим» нельзя.
   */
  channelUrl?: string;
}

/**
 * `GET /achievements` → AchievementDto.
 *
 * Ачивка одноразовая и сквозная на сезон: выполненная не сбрасывается
 * при смене Месячного цикла (ГДД §9.1). Условий может быть несколько,
 * и завершается она только когда выполнены ВСЕ.
 */
export interface Achievement {
  id: number;
  code: string;
  title: string;
  description: string;
  iconUrl?: string;
  rewardPoints: number;
  /**
   * Награда Фрибетами сверх Очков; 0 — без Фрибетов. Право фиксируется
   * при выполнении и от подписки не зависит — подписку и номер
   * проверяет выдача.
   */
  rewardFreebets: number;
  status: AchievementStatus;
  completedAt?: string;
  /** Участвует в отборе на Финальный розыгрыш сезона (ГДД §11.3). */
  countsForSeasonFinal: boolean;
  /** «Крутая» Ачивка: в интерфейсе у неё своя метка (`is_legendary`). */
  isLegendary: boolean;
  conditions: AchievementCondition[];
}

/* ── Фрибеты ─────────────────────────────────────────────────────────── */

export const FreebetPayoutStatus = {
  AWAITING_CONDITIONS: "AWAITING_CONDITIONS",
  READY: "READY",
  SENDING: "SENDING",
  CONFIRMED: "CONFIRMED",
  FAILED: "FAILED",
  PHONE_NOT_LINKED: "PHONE_NOT_LINKED",
  EXPIRED: "EXPIRED",
} as const;

export type FreebetPayoutStatus =
  (typeof FreebetPayoutStatus)[keyof typeof FreebetPayoutStatus];

/**
 * `GET /freebets` → FreebetsStatusDto.
 *
 * Счётчик растёт в момент ФИКСАЦИИ права — при закрытии недели или итогах
 * Месячного розыгрыша, — а выдача идёт после конца цикла (ГДД §4.4).
 * Поэтому ненулевой `earnedThisCycle` без заявки это норма, а не ошибка.
 */
export interface FreebetsStatus {
  earnedThisCycle: number;
  /** Статус заявки. Нет — заявка ещё не создана. */
  payoutStatus?: FreebetPayoutStatus;
  payoutAmount?: number;
  hasPhone: boolean;
  isSubscribed: boolean;
  /**
   * Показывать кнопку «Получить». Только после подтверждения букмекером:
   * нажатие НЕ является начислением, оно лишь открывает диплинк (ГДД §4.4).
   */
  canClaim: boolean;
  /** Срок выполнения условий — 7 дней после конца цикла. */
  claimDeadlineAt?: string;
}

/** `POST /freebets/claim` → ClaimResponseDto. */
export interface ClaimResult {
  /** Пустая строка — диплинк ещё не задан в настройках. */
  deeplink: string;
}

/* ── мелкие тела и ответы ────────────────────────────────────────────── */

/** `PATCH /users/me/display-name`. Длина ника — от 2 до 32 символов. */
export interface UpdateDisplayNamePayload {
  displayName: string;
}

/** `POST /predictions/popup/seen` → MarkSeenResponseDto. */
export interface MarkSeenResult {
  /** Сколько результатов помечено просмотренными. */
  marked: number;
}
